import {EExtType, ENavLanguages, EStage, JDownloadDep, JDownloadLocInfos, JDownloadProductMetas, JDownloadProductSource, L10nLabels} from "../../commons/api";
import {lang, ScSoftwareComponent} from "../../commons/language-selector";
import {SvcESSearch} from "wfr/search/essearch";
import {JHitSource} from "wfr/search/searchapi"
import {Dict} from "wfr/commons/common";
import {JSX} from "wfr/commons/jsx"
import {ETileTitle, ProductTile} from "../product-tile";
import {qs, styleSheet, updateHistory} from "wfr/commons/core";
import {filterLastKeys, sort} from "../commons";

export {DownloadSearchPanel}

const search_langs = ["fr", "en", "es", "ar", "pt", "it", "de", "el", "nl"]
const enum ECats {
	nature = "nature",
	productKey = "productKey",
	extProductKey = "extProductKey"
}

const	labels:L10nLabels = {
	[ENavLanguages.fr]: {
		showAllVersions:"Afficher toutes les versions",
		total:(size)=>`${size} résultat${size==1?"":"s"}`,
		application_dedicated:"Applications métier",
		application_generic:"Applications Scenari génériques",
		server_dedicated:"Serveurs métier",
		server_generic:"Serveurs Scenari génériques",
		deps:"Application",
		extProductKeyCat:(title:string)=> `Extensions pour ${title}`,
		skin:"Skin",
		gen:"Générateur",
		mod:"Enrichissement du modèle",
		lastMinor:lang=>`Version mineure plus récente non disponible pour la langue ${lang} : `,
		last:lang => `Version plus récente non disponible pour la langue ${lang} : `,
		depLabel: "Compatible avec"
	},
	[ENavLanguages.en]:{
		showAllVersions:"Show all versions",
		total:(size)=>`${size} result${size==1?"":"s"}`,
		application_dedicated:"Dedicated applications",
		application_generic:"Generic Scenari applications",
		server_dedicated:"Dedicated servers",
		server_generic:"Generic Scenari servers",
		deps:"Application",
		extProductKeyCat:(title:string)=> `Extensions of ${title}`,
		skin:"Skin",
		gen:"Generator",
		mod:"Model enhancement",
		lastMinor:lang=>`Latest minor version not available for ${lang} language: `,
		last:lang=>`Latest version not available for ${lang} language: `,
		depLabel: "Compatible with"
	}
}

interface JHitProductInnerHit<T,I> extends JHitSource<T>{
	inner_hits:{
		"product":{
			"hits":{
				"total":{
					value:number,
					relation:string
				}
				//TODO -> 1 unique résultat mineur ?
				hits:JHitSource<I>[]
			}
		}
	}
}

interface JLangSource{
	lang:string
}
interface JPKBucket{
	key:string,
	doc_count:number,
	majorVersion:{
		"buckets":JMJBucket[]
	}
}
interface JMJBucket{
	key:number,
	doc_count:number,
	minorVersion:{
		"hits":{
			total:{value:number, relation:string},
			hits:JHitProductInnerHit<JDownloadProductSource, JLangSource>[]
		}
	}
}

/**
 * Version normalisée avec la liste des langues disponibles
 */
interface JDownloadProductSourceLangs extends JDownloadProductSource{
	langs:string[]
	normLabel:string //productKey normalisé en minuscule
}
interface JDownloadProductSourceLangsWithDeps extends JDownloadProductSourceLangs{
	deps:JDownloadDep[]
}

interface JProductAgg{
	buckets:JPKBucket[]
}

interface JTermAgg{
	buckets:JTermBucket[]
	doc_count_error_upper_bound:number
	sum_other_doc_count:number
}
interface JTermBucket{
	doc_count:number
	key:string
}


/**
 * Classe de base d'un Filter.
 */
abstract class DownloadFilter extends ScSoftwareComponent{
	protected labels = labels

	abstract resetable():boolean
	abstract reset():void

	// Completion des propriétés à envoyer à ES
	getFilterRequest(props:Dict<any>):any{
		return props;
	}
	// Filtrage des résultats post requête
	filterResults(keys:string[], products:Dict<JDownloadProductSourceLangs>):string[]{
		return keys
	}
	//Ajout d'une catégorie pour la présentation des résultats
	addResultsCat(resultsCat: ECats[]): ECats[]{
		return resultsCat;
	}
	feedQsProp(qsProps:Dict<any>):Dict<any>{
		return  qsProps;
	}
	protected qs():Dict<any>{
		const panel = this.closest("div.tab")
		if(qs().panel == panel.id) return qs()
		else return {}
	}
}

class TextDownloadFilter extends DownloadFilter{
	rebuild(): boolean {
		if(!super.rebuild()) return false
		this.shadowRoot.append(<input type="text" value={this.qs().text || ""}/>)
		const input = this.shadowRoot.querySelector("input")
		input.addEventListener("keyup", event=>this.dispatchEvent(new CustomEvent("filter-change", {detail:{"txt":input.value}, bubbles:true})))
		return true
	}

	filterResults(keys: string[], products: Dict<JDownloadProductSourceLangs>): string[] {
		const filtered:string[] = []
		const filter = (this.shadowRoot.querySelector("input") as HTMLInputElement).value.trim().toLowerCase()
		if(filter == "") return keys

		keys.forEach(key=> {
			if(products[key].normLabel.indexOf(filter) != -1) filtered.push(key)
		})
		return filtered
	}

	resetable(): boolean {
		return (this.shadowRoot.querySelector("input") as HTMLInputElement).value.trim().length > 0
	}
	reset(): void {
		(this.shadowRoot.querySelector("input") as HTMLInputElement).value = "";
	}
	feedQsProp(qsProps:Dict<any>){
		const text = this.shadowRoot.querySelector("input").value
		if(text) qsProps.text = text
		return qsProps
	}
}
class AllVersionDownloadFilter extends DownloadFilter{
	rebuild(): boolean {
		if(!super.rebuild()) return false
		this.shadowRoot.append(<label>{this.s("showAllVersions")}<input type="checkbox" checked={this.qs().allVersions}/></label>)
		const input = this.shadowRoot.querySelector("input")
		input.addEventListener("change", event=>this.dispatchEvent(new CustomEvent("filter-change", {detail:{"allVersions":input.checked}, bubbles:true})))
		return true
	}

	filterResults(keys: string[], products: Dict<JDownloadProductSourceLangs>): string[] {
		if(this.shadowRoot.querySelector("input:checked")) return keys;
		else return filterLastKeys(keys, products)
	}

	addResultsCat(resultsCat: ECats[]): ECats[] {
		if(this.shadowRoot.querySelector("input:checked")) resultsCat.push(ECats.productKey);
		return resultsCat;
	}

	resetable(): boolean {
		return this.shadowRoot.querySelector("input:checked") != null
	}
	reset(): void {
		(this.shadowRoot.querySelector("input") as HTMLInputElement).checked = false;
	}
	feedQsProp(qsProps:Dict<any>){
		const allVersions = this.shadowRoot.querySelector("input").checked
		if(allVersions) qsProps.allVersions = allVersions
		return qsProps
	}
}
class AllExtVersionDownloadFilter extends AllVersionDownloadFilter{
	filterResults(keys: string[], products: Dict<JDownloadProductSourceLangs>): string[] {
		if(this.shadowRoot.querySelector("input:checked")) return keys;
		const appProducts = (document.querySelector("div#appSearch download-search-panel") as DownloadSearchPanel).products
		//Step 1, on filtre les keys par leur dépendances
		const lastDeps:string[] = filterLastKeys(Object.keys(appProducts), appProducts)
		const filteredByLastDep:string[] = []
		keys.forEach(key=>{
			const product:JDownloadProductSourceLangsWithDeps = products[key] as JDownloadProductSourceLangsWithDeps
			product.deps.forEach(dep=>{
				if(lastDeps.indexOf(dep.product) != -1) filteredByLastDep.push(key)
			});
		});
		return filterLastKeys(filteredByLastDep, products)
	}
	addResultsCat(resultsCat: ECats[]): ECats[] {
		resultsCat.push(ECats.extProductKey);
		return resultsCat
	}
}
class LangDownloadFilter extends DownloadFilter{
	rebuild(): boolean {
		if(!super.rebuild()) return false
		this.shadowRoot.append(<select>
			<option value="">{this.s("l10n")}</option>
			{search_langs.map(lang=>{return <option value={lang} selected={this.qs().lang == lang}>{this.s(`l10n_${lang}`)}</option>})}
		</select>)
		const select = this.shadowRoot.querySelector("select")
		select.addEventListener("change", event=>this.dispatchEvent(new CustomEvent("filter-change", {detail:{"lang":select.value}, bubbles:true})))

		return true
	}
	getFilterRequest(props:Dict<any>) {
		const value = (this.shadowRoot.querySelector("select") as HTMLSelectElement).value
		if(value) props.lang = [value, `${value}-*`]
		return props
	}

	filterResults(keys: string[], products: Dict<JDownloadProductSourceLangs>): string[] {
		const select = this.shadowRoot.querySelector("select")
		const value = select.value
		select.innerHTML = ``
		select.append(<option value="" selected={""==value}>{this.s("l10n")}</option>)
		select.append(...search_langs.map(lang=>{return <option value={lang} selected={lang==value}>{this.s(`l10n_${lang}`)}</option>}))

		const panel = this.closest("download-search-panel") as DownloadSearchPanel
		const paths:any = {}
		keys.forEach(product=>paths[panel.products[product].productKey] = panel.products[product].path.split("@").shift()+"@*")

		panel.esSearch.search("languageAgg", {path:Object.values(paths)}).then(resp=>{
			const agg = resp.agg.lang as JTermAgg
			const langs:Dict<boolean> = {}
			agg.buckets.forEach(bucket=>{
				if(bucket.key !== "all") langs[bucket.key.split("-").shift()] = true;
			});
			this.shadowRoot.querySelectorAll("option").forEach(option=>{
				if(option.value !== "" && !langs[option.value]) option.remove()
			});
		})
		return keys
	}
	resetable(): boolean {
		return (this.shadowRoot.querySelector("select") as HTMLSelectElement).value.length > 0
	}
	reset(): void {
		(this.shadowRoot.querySelector("select") as HTMLSelectElement).value = "";
	}
	feedQsProp(qsProps:Dict<any>){
		const lang = this.shadowRoot.querySelector("select").value
		if(lang) qsProps.lang = lang
		return qsProps
	}
	getLabel(){
		const lang = this.shadowRoot.querySelector("select").value
		return this.s(`l10n_${lang}`)
	}
}
class ExtTypeDownloadFilter extends DownloadFilter{
	rebuild(): boolean {
		if(!super.rebuild()) return false

		Object.keys(EExtType).forEach(key=>{
			this.shadowRoot.append(<label>{this.s(key)} <input type="checkbox" value={key} checked={this.qs().extType && this.qs().extType.indexOf(key)!=-1}/></label> )
		})
		this.shadowRoot.querySelectorAll("input").forEach(input=> input.addEventListener("change", event=>this.dispatchEvent(new CustomEvent("filter-change", {detail:{exttype:input.value}, bubbles:true}))))
		return true
	}

	resetable(): boolean {
		return this.shadowRoot.querySelectorAll("input:checked").length > 0
	}
	reset(): void {
		this.shadowRoot.querySelectorAll("input:checked").forEach(input=>(input as HTMLInputElement).checked = false)
	}

	filterResults(keys: string[], products: Dict<JDownloadProductSourceLangs>): string[] {
		const filter:EExtType[] = []
		this.shadowRoot.querySelectorAll("input:checked").forEach(input=>filter.push((input as HTMLInputElement).value as EExtType))
		if(filter.length == 0) return keys

		const filtered:string[] = []
		keys.forEach(key=> {
			if(filter.indexOf(products[key].extType) != -1) filtered.push(key)
		})
		return filtered
	}
	feedQsProp(qsProps:Dict<any>){
		const extType: EExtType[] = []
		this.shadowRoot.querySelectorAll("input:checked").forEach(input=>extType.push((input as HTMLInputElement).value as EExtType))
		if(extType.length > 0) qsProps.extType = extType
		return qsProps
	}
}

class DepsDownloadFilter extends DownloadFilter{
	onChange :boolean = false
	rebuild(): boolean {
		if(!super.rebuild()) return false
		this.shadowRoot.append(<select>
			<option value="">{this.s("deps")}</option>
		</select>)
		const select = this.shadowRoot.querySelector("select")
		select.addEventListener("change", event=>{
			this.onChange = true;
			this.dispatchEvent(new CustomEvent("filter-change", {detail:{"dep":select.value}, bubbles:true}))
		})
		return true
	}

	protected refine(keys: string[], products: Dict<JDownloadProductSourceLangs>) {
		const values:Dict<boolean> = {}
		const appProducts = (document.querySelector("div#appSearch download-search-panel") as DownloadSearchPanel).products
		keys.forEach(key=>{
			const product:JDownloadProductSourceLangsWithDeps = products[key] as JDownloadProductSourceLangsWithDeps
			product.deps.forEach(dep=>{
				if(appProducts[dep.product]) values[dep.product] = true
			});
		});

		const select = this.shadowRoot.querySelector("select") as HTMLSelectElement
		const value = this.onChange ? select.value : select.value || this.qs().dep
		this.onChange = false
		select.innerHTML = ""
		select.append(<option value="">{this.s("deps")}</option>)
		select.append(...(Object.keys(values).sort(productSort).map(key=>{return <option value={key} selected={key==value}>{`${appProducts[key].downloadLocInfos[lang]?.title || appProducts[key].productKey} ${appProducts[key].majorVersion}`}</option>})));
	}
	filterResults(keys: string[], products: Dict<JDownloadProductSourceLangs>): string[] {
		this.refine(keys, products)
		const select = (this.shadowRoot.querySelector("select") as HTMLSelectElement).value
		if(!select) return keys;
		else{
			const filtered:string[] = []
			keys.forEach(key=>{
				const product:JDownloadProductSourceLangsWithDeps = products[key] as JDownloadProductSourceLangsWithDeps
				product.deps.forEach(dep=>{
					if(dep.product == select) filtered.push(key)
				})
			});
			return filtered
		}
	}

	resetable(): boolean {
		return (this.shadowRoot.querySelector("select") as HTMLSelectElement).value.length > 0
	}
	reset(): void {
		this.onChange = true;
		(this.shadowRoot.querySelector("select") as HTMLSelectElement).value = "";
	}
	feedQsProp(qsProps:Dict<any>){
		const dep = this.shadowRoot.querySelector("select").value
		if(dep) qsProps.dep = dep
		return qsProps
	}
}

class DownloadSearchPanel extends ScSoftwareComponent {
	public products:Dict<JDownloadProductSourceLangs> = {} // Public pour que le search App expose les labels des produits pour le filter des extension
	public esSearch:SvcESSearch<JHitProductInnerHit<JDownloadProductSource, JLangSource>>

	protected request_code:string
	protected filters:DownloadFilter[] = []

	protected queryProducts:Dict<JDownloadProductSourceLangs> = null // Sous ensemble de products obtenu lors d'une requête avec props
	protected resultsCat:ECats[] // Liste des catégories à exploiter pour afficher les résultats

	protected productsLocInfosByPk:Dict<JDownloadLocInfos> = {} // Index pour affichage des catégorie de productKey si toutes les versions
	protected current_keys:string //Hash des keys actuellement constuites (keys stringifié) pour éviter de reconstruire les réponse si pas de changement du nombre de keys à construire

	protected initiated:boolean = false
	protected connectedCB:boolean = false

	protected urlTree:string
	protected labels = labels

	constructor() {
		super();
		this.attachShadow({mode: 'open'})
		this.shadowRoot.append(<div class="head">
			<slot name="filters"/></div>,
			<div class="results"/>)
	}

	static_init(ut:string, es:string){
		this.esSearch = new SvcESSearch(es)
		this.urlTree = ut
		this.initiated = true
		this.load().then()
	}

	async connectedCallback(): Promise<void> {
		this.shadowRoot!.adoptedStyleSheets.push(await styleSheet("./css/components.css"))
		this.addEventListener("filter-change", event=>this.search());
		this.request_code = this.getAttribute("request")
		this.querySelectorAll("[slot='filters'] *").forEach(filter=>{this.filters.push(filter as DownloadFilter)});

		this.connectedCB = true
		this.load().then()
	}

	protected async load(){
		if(!this.initiated || !this.connectedCB) return;

		this.products = await this.request(null);
		Object.values(this.products).forEach(product=>{
			if(!this.productsLocInfosByPk[product.productKey]) this.productsLocInfosByPk[product.productKey] = product.downloadLocInfos
		})
		this.initiated = true;
		this.dispatchEvent(new CustomEvent("search-initiated", {detail:{query:this.request_code}, bubbles:true}))
		this.rebuild()
		await this.search()
	}

	protected async request(props:Dict<any>){
		const products:Dict<JDownloadProductSourceLangs> = {}
		const productAgg = (await this.esSearch.search(this.request_code, props)).agg.productKey as JProductAgg
		productAgg?.buckets.forEach(pkBucket=>{
			pkBucket.majorVersion.buckets.forEach(versionBucket=>{
				const productSource:JDownloadProductSourceLangs = versionBucket.minorVersion.hits.hits[0]._source as JDownloadProductSourceLangs
				const langs:Dict<boolean> = {}
				productSource.normLabel = `${productSource.downloadLocInfos[lang]?.title || productSource.productKey} ${productSource.majorVersion}.${productSource.minorVersion} ${this.s(productSource.stage)}`.toLowerCase()
				versionBucket.minorVersion.hits.hits[0].inner_hits.product.hits.hits.forEach(langHit=>{
					if(langHit._source.lang) langs[langHit._source.lang.split("-")[0]] = true;
				})
				productSource.langs = Object.keys(langs)
				products[`${productSource.productKey}@${productSource.majorVersion}`] = productSource
			})
		})
		return products
	}

	rebuild(): boolean {
		if(!this.initiated) return

		this.shadowRoot.querySelector("div.head").append(<button onclick={this.resetFilters} title={this.s("reset")} disabled="" class="reset">
			<span>{this.s("reset")}</span>
		</button>)
		this.filters.forEach(filter=> (filter as unknown as ScSoftwareComponent).rebuild())
	}

	async search(){
		//1. Init de queryProducts.
		let props = {}
		this.filters.forEach(filter=>{ props = filter.getFilterRequest(props)})
		//Si un filter a ajouté une prop de recherche, on fait une requête ES.
		if(Object.keys(props).length > 0) {
			this.queryProducts = await this.request(props);
			// nouvelle recherche, on ne peut pas savoir si les keys récupérées pointent les mêmes versions mineures. On force la reconstruction
			this.current_keys = null;
		}
		//Sinon, queryProducts = products
		else if(this.queryProducts !== this.products){
			this.queryProducts = this.products;
			//On force la reconstruction des résulats
			this.current_keys = null;
		}

		//2. Filtre postérieur à la requête des keys
		let keys = Object.keys(this.queryProducts)
		this.filters.sort((a, b)=>{
			// Le DepsDownloadFilter a besoin d'être constuit en dernier pour n'afficher que les valeurs présentes une fois les autres filtres appliqués
			if(a instanceof LangDownloadFilter) return 1;
			if(b instanceof LangDownloadFilter) return -1;
			else return a instanceof DepsDownloadFilter ? 1:-1
		}).forEach(filter=> keys = filter.filterResults(keys, this.queryProducts));
		const stringifiedKeys = JSON.stringify(keys)

		//Maj QS
		if(!(this.closest("div.tab") as HTMLDivElement).hidden) {
			let qsProps: Dict<any> = {}
			this.filters.forEach(filter => qsProps = filter.feedQsProp(qsProps))
			qsProps.panel = this.closest("div.tab").id
			updateHistory(qsProps, {replaceHist: true})
		}

		// MAj reset button
		let resetable = false;
		this.filters.forEach(filter=>{ resetable = filter.resetable() || resetable})
		if(resetable) (this.shadowRoot.querySelector("button.reset") as HTMLButtonElement).disabled = false
		else (this.shadowRoot.querySelector("button.reset") as HTMLButtonElement).disabled = true

		if(stringifiedKeys == this.current_keys) return;
		this.current_keys = stringifiedKeys

		//3. Init des catégories de présentation des résultats
		this.resultsCat = []
		if(this.request_code == "appSearch" || this.request_code == "serverSearch")
			this.resultsCat.push(ECats.nature)
		this.filters.forEach(filter=>filter.addResultsCat(this.resultsCat))

		//4 Affichage des résulats
		const results = this.shadowRoot.querySelector("div.results")
		results.innerHTML = ""
		results.append(<div class="head"><span>{this.s("total", keys.length)}</span></div>)

		let cat = this.resultsCat.shift() as ECats
		let subcat = this.resultsCat.shift() as ECats

		if(cat && subcat) {
			this.listPopulatedCats(cat, keys, this.queryProducts).forEach(catKey=>{
				const filteredProducts = this.filterProductsByCat(cat, catKey, keys, this.queryProducts)
				results.append(<div class={cat}>
					<h3><span>{this.getCatLabel(cat, catKey)}</span></h3>
					{this.buildSubCat(subcat, filteredProducts)}
				</div> )
			})
		}
		else if(cat) results.append(...this.buildSubCat(cat, keys))
		else results.append(this.buildTileProducts(keys.sort((a,b)=>productResultSort(this.queryProducts[a], this.queryProducts[b])), true))
	}

	// Construit la liste des catégorie de niveaux 2 et leurs tuiles respectives
	buildSubCat(cat:ECats, keys:string[]){
		return this.listPopulatedCats(cat, keys, this.queryProducts).map(catKey=>{
			const products = this.filterProductsByCat(cat, catKey, keys, this.queryProducts).sort((a,b)=>productResultSort(this.queryProducts[a], this.queryProducts[b]));
			return <div class={`${catKey} open closable`}>
				<div class="catHead">
					<h4><span>{this.getCatLabel(cat, catKey)}</span></h4>
					<button class="close" title={this.s("close")} onclick={this.toggleCat}><span>{this.s("close")}</span></button>
				</div>
				<div class="products">
					{cat == ECats.productKey ? <div class="tagline">{this.queryProducts[products[0]].downloadLocInfos[lang]?.tagline||""}</div>:null}
					{this.buildTileProducts(products, cat != ECats.productKey)}
				</div>
			</div>
		})
	}

	buildTileProducts(keys:string[], tagline:boolean){
		const appProducts = (document.querySelector("div#appSearch download-search-panel") as DownloadSearchPanel).products
		const devProducts = (document.querySelector("div#devSearch download-search-panel") as DownloadSearchPanel).products
		const extProducts = this.products

		return <ul>{keys.map(key=> {
			const missingLastVersionKey = this.getMissingLastVersion(key);
			return <li>
				{new ProductTile(this.queryProducts[key], this.urlTree, {title:ETileTitle.short, fullDatedVersion:true, tagline:tagline, addClassName:"tile"})}
				{this.queryProducts[key].extType?<div class="deps"><span class="title">{this.s("depLabel")}</span> {
					(this.queryProducts[key] as JDownloadProductSourceLangsWithDeps).deps.map((dep, i) => {
						const depProduct = appProducts[dep.product] || devProducts[dep.product] || extProducts[dep.product]
					  return [i != 0 ? <span>/</span>:null, new ProductTile(depProduct, this.urlTree, {title:ETileTitle.dep, fromMinorVersion:dep.firstMinorVersion.toString(), addClassName:"inline"})]
					})}
				</div>:null}
				{this.queryProducts[key].minorVersion != this.products[key].minorVersion || missingLastVersionKey ? <div class="langs">
					{this.queryProducts[key].minorVersion != this.products[key].minorVersion ? <div class="lastMinorVersionWarning">
						<span>{this.s("lastMinor", (this.querySelector("lang-download-filter") as LangDownloadFilter).getLabel())}</span>
						{new ProductTile(this.products[key], this.urlTree, {title:ETileTitle.long, addClassName:"inline", noLogo:true})}
					</div> : null}
					{missingLastVersionKey ? <div class="lastVersionWarning">
						<span>{this.s("last", (this.querySelector("lang-download-filter") as LangDownloadFilter).getLabel())}</span>
						{new ProductTile(this.products[missingLastVersionKey], this.urlTree, {title:ETileTitle.short, addClassName:"inline", noLogo:true})}
					</div>:null}
				</div>:null}
			</li>})}
		</ul>
	}

	getMissingLastVersion(key:string):string{
		const keyParts = key.split("@")
		const pk = keyParts[0]
		const major = parseFloat(keyParts[1])
		let foundMissingKey:string = null
		Object.keys(this.products).forEach(key=>{
			if(this.products[key].productKey != pk) return;
			if(parseFloat(this.products[key].majorVersion) <= major) return;
			if(this.queryProducts[key]) return;
			if(foundMissingKey != null && parseFloat(foundMissingKey.split("@")[1]) > major) return;
			foundMissingKey = key;
		})
		return foundMissingKey
	}

	//Recupère la liste des catégories "populées" dans une liste de keys triées
	listPopulatedCats(cat:ECats, keys:string[], products:Dict<JDownloadProductSourceLangs>) {
		const cats:Dict<boolean> = {}
		let sortedKeys:string[]
		if(cat == ECats.extProductKey) sortedKeys = keys.sort(productSort)
		else sortedKeys = keys.sort()
		sortedKeys.forEach(pk=>{
			const product = products[pk]
			if(cat == ECats.extProductKey) {
				const appProducts = (document.querySelector("div#appSearch download-search-panel") as DownloadSearchPanel).products
				const productDep: JDownloadProductSourceLangsWithDeps = product as JDownloadProductSourceLangsWithDeps
				let found = false;
				productDep.deps.forEach(dep => {
					if(appProducts[dep.product]) cats[dep.product] = true
				})
			}
			else if (product[cat]) cats[product[cat]] = true;
		});
		return Object.keys(cats).sort()
	}

	//récupère la liste des keys qui matchent la cat et catKey
	filterProductsByCat(cat:ECats, catKey:string, keys:string[], products:Dict<JDownloadProductSourceLangs>) {
		const filteredProducts:string[] = []
		keys.forEach(pk=>{
			const product = products[pk]
			if(cat == ECats.extProductKey){
				const productDep:JDownloadProductSourceLangsWithDeps = product as JDownloadProductSourceLangsWithDeps
				let found = false;
				productDep.deps.forEach(dep=>{ if(dep.product == catKey) found = true})
				if(found) filteredProducts.push(pk)
			}
			else if(product[cat] == catKey) filteredProducts.push(pk);
		});
		return filteredProducts;
	}

	protected getCatLabel(cat:ECats, val:string){
		if(cat == ECats.nature) return this.s(`${this.request_code == "appSearch" ? "application" : "server"}_${val}`)
		else if (cat == ECats.productKey) return this.productsLocInfosByPk[val][lang].title
		else if(cat == ECats.extProductKey) {
			const appProducts = (document.querySelector("div#appSearch download-search-panel") as DownloadSearchPanel).products
			return this.s("extProductKeyCat", `${appProducts[val].downloadLocInfos[lang]?.title|| appProducts[val].productKey} ${appProducts[val].majorVersion}`)
		}
		return ""
	}

	toggleCat(event:MouseEvent){
		const dsp = (this.getRootNode() as ShadowRoot).host as DownloadSearchPanel
		const content = this.parentElement.nextElementSibling;
		if(this.classList.contains("open")) {
			this.classList.remove("open")
			this.classList.add("close")
			this.setAttribute("title", dsp.s("close"))
			this.innerHTML = `<span>${dsp.s("close")}</span>`
			content.removeAttribute("hidden")
		}
		else {
			this.classList.remove("close")
			this.classList.add("open")
			this.setAttribute("title", dsp.s("open"))
			this.innerHTML = `<span>${dsp.s("open")}</span>`
			content.setAttribute("hidden", "true");
		}
	}

	resetFilters(){
		const dsp = (this.getRootNode() as ShadowRoot).host as DownloadSearchPanel
		dsp.filters.forEach(filter=>filter.reset())
		dsp.search()
	}
}

const productSort = function(a:string, b:string){
	const as = a.split("@")
	const bs = b.split("@")
	if(as[0] == bs[0]) return parseFloat(as[1]) < parseFloat(bs[1]) ? 1:-1;
	else return as[0] < bs[0] ? -1:1;
}

const productResultSort = function(a:JDownloadProductSource, b:JDownloadProductSource){
	const keyA = a.sortKey || a.productKey
	const keyB = b.sortKey || b.productKey
	if(keyA != keyB) return keyA > keyB ? 1 : -1
	return sort(a,b)
}

customElements.define('all-versions-download-filter', AllVersionDownloadFilter)
customElements.define('all-ext-versions-download-filter', AllExtVersionDownloadFilter)
customElements.define('deps-download-filter', DepsDownloadFilter)
customElements.define('text-download-filter', TextDownloadFilter)
customElements.define('lang-download-filter', LangDownloadFilter)
customElements.define("exttype-download-filter", ExtTypeDownloadFilter)
customElements.define('download-search-panel', DownloadSearchPanel)